/**
 * mediaStore - persistent blob store for user-attached chat media (adapter).
 *
 * User photos/videos are large and unsuitable for localStorage, so we keep the
 * binary blobs in IndexedDB and only persist a lightweight `blobId` reference
 * inside the chat messages. If IndexedDB is unavailable (private mode, old
 * WebView) we transparently fall back to an in-memory map for the session.
 *
 * The public API is async and Promise-based:
 *   putBlob(blob)      -> Promise<string blobId>
 *   getBlobUrl(blobId) -> Promise<string objectURL | null>
 *   deleteBlob(blobId) -> Promise<void>
 *
 * Callers are responsible for revoking object URLs they no longer need via
 * URL.revokeObjectURL to avoid leaks.
 */
const DB_NAME = 'vizosMedia';
const STORE = 'blobs';
const VERSION = 1;

/** In-memory fallback when IndexedDB is not usable. */
const memory = new Map();

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexeddb-unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('indexeddb-open-failed'));
  });
  return dbPromise;
}

const newId = () => `m_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;

/** Store a blob; returns a stable id to reference it later. */
export async function putBlob(blob) {
  const id = newId();
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(blob, id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    memory.set(id, blob);
  }
  return id;
}

/** Resolve a stored blob to a fresh object URL, or null if missing. */
export async function getBlobUrl(id) {
  if (!id) return null;
  let blob = memory.get(id) || null;
  if (!blob) {
    try {
      const db = await openDb();
      blob = await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const r = tx.objectStore(STORE).get(id);
        r.onsuccess = () => resolve(r.result || null);
        r.onerror = () => reject(r.error);
      });
    } catch {
      blob = null;
    }
  }
  return blob ? URL.createObjectURL(blob) : null;
}

/** Remove a stored blob (best-effort). */
export async function deleteBlob(id) {
  if (!id) return;
  memory.delete(id);
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* ignore */
  }
}
